import * as React from 'react';
import {
  getTokens,
  getUser,
  clearTokens,
  callLogoutApi,
  AUTH_UPDATED_EVENT,
  getMsUntilExpiry,
  isSessionExpired,
  parseRealmRolesFromAccessToken,
} from './authUtils';
import type { AuthUser } from './authUtils';
import { AUTH_UNAUTHORIZED_EVENT, abortAllRequests } from '../api/http';
import { APP_ROLES, type AppRole } from './role-based-access-control';

type LogoutReason = 'manual' | 'expired' | 'unauthorized' | 'idle';

interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | null;
  realmRoles: string[];
  hasRole: (role: string) => boolean;
  logout: (reason?: LogoutReason) => void;
  isDevRoleOverrideEnabled: boolean;
  devRoleOverride: AppRole | null;
  setDevRoleOverride: (role: AppRole | null) => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);
const DEV_ROLE_OVERRIDE_KEY = 'csp_dev_role_override';
const APP_ROLE_VALUES = Object.values(APP_ROLES) as AppRole[];

function toAppRole(value: string | null | undefined): AppRole | null {
  if (!value) return null;
  return APP_ROLE_VALUES.includes(value as AppRole) ? (value as AppRole) : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const isDevRoleOverrideEnabled =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_DEV_ROLE_OVERRIDE_ENABLED === 'true';
  const defaultDevRoleOverride = toAppRole(process.env.REACT_APP_DEV_ROLE_OVERRIDE);
  const [devRoleOverride, setDevRoleOverrideState] = React.useState<AppRole | null>(() => {
    if (!isDevRoleOverrideEnabled) return null;
    try {
      const stored = window.localStorage.getItem(DEV_ROLE_OVERRIDE_KEY);
      return toAppRole(stored) ?? defaultDevRoleOverride;
    } catch {
      return defaultDevRoleOverride;
    }
  });

  React.useEffect(() => {
    if (!isDevRoleOverrideEnabled) return;
    try {
      if (devRoleOverride) {
        window.localStorage.setItem(DEV_ROLE_OVERRIDE_KEY, devRoleOverride);
      } else {
        window.localStorage.removeItem(DEV_ROLE_OVERRIDE_KEY);
      }
    } catch {
      // ignore storage access errors
    }
  }, [devRoleOverride, isDevRoleOverrideEnabled]);

  const setDevRoleOverride = React.useCallback((role: AppRole | null) => {
    setDevRoleOverrideState(role);
  }, []);

  const applyDevRoleOverride = React.useCallback((roles: string[]): string[] => {
    if (!isDevRoleOverrideEnabled || !devRoleOverride) return roles;
    const nonAppRoles = roles.filter((role) => !APP_ROLE_VALUES.includes(role as AppRole));
    return [...nonAppRoles, devRoleOverride];
  }, [devRoleOverride, isDevRoleOverrideEnabled]);

  const getRealmRolesFromSession = React.useCallback((): string[] => {
    const tokens = getTokens();
    if (!tokens?.access_token) return [];
    return parseRealmRolesFromAccessToken(tokens.access_token);
  }, []);

  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(() =>
    Boolean(getTokens())
  );
  const [user, setUser] = React.useState<AuthUser | null>(() => getUser());
  const [realmRoles, setRealmRoles] = React.useState<string[]>(() =>
    applyDevRoleOverride(getRealmRolesFromSession())
  );

  React.useEffect(() => {
    const sync = () => {
      const tokens = getTokens();
      setIsAuthenticated(Boolean(tokens));
      setUser(getUser());
      setRealmRoles(
        tokens?.access_token
          ? applyDevRoleOverride(parseRealmRolesFromAccessToken(tokens.access_token))
          : []
      );
    };
    sync();
    window.addEventListener('storage', sync);
    window.addEventListener(AUTH_UPDATED_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(AUTH_UPDATED_EVENT, sync);
    };
  }, [applyDevRoleOverride]);

  const performClientLogout = React.useCallback((reason: LogoutReason = 'manual') => {
    abortAllRequests();
    clearTokens();
    setIsAuthenticated(false);
    setUser(null);
    setRealmRoles([]);
    const reasonQuery =
      reason === 'manual' ? '' : `?reason=${encodeURIComponent(reason)}`;
    window.location.href = `/signin${reasonQuery}`;
  }, []);

  const logout = React.useCallback((reason: LogoutReason = 'manual') => {
    if (reason === 'manual') {
      callLogoutApi().finally(() => {
        performClientLogout(reason);
      });
      return;
    }
    performClientLogout(reason);
  }, [performClientLogout]);

  const hasRole = React.useCallback(
    (role: string) => realmRoles.includes(role),
    [realmRoles]
  );

  const value = React.useMemo(
    () => ({
      isAuthenticated,
      user,
      realmRoles,
      hasRole,
      logout,
      isDevRoleOverrideEnabled,
      devRoleOverride,
      setDevRoleOverride,
    }),
    [
      isAuthenticated,
      user,
      realmRoles,
      hasRole,
      logout,
      isDevRoleOverrideEnabled,
      devRoleOverride,
      setDevRoleOverride,
    ]
  );

  React.useEffect(() => {
    const onUnauthorized = () => logout('unauthorized');
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [logout]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    const msUntilExpiry = getMsUntilExpiry();
    if (msUntilExpiry === null) return;
    if (msUntilExpiry <= 0) {
      logout('expired');
      return;
    }
    const timerId = window.setTimeout(() => logout('expired'), msUntilExpiry);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [isAuthenticated, logout]);

  React.useEffect(() => {
    const checkExpiry = () => {
      if (!getTokens()) return;
      if (isSessionExpired()) logout('expired');
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };
    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [logout]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    const idleTimeoutMs = Number(process.env.REACT_APP_IDLE_TIMEOUT_MS ?? 15 * 60 * 1000);
    if (!Number.isFinite(idleTimeoutMs) || idleTimeoutMs <= 0) return;

    let idleTimerId: number | null = null;
    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'touchstart',
    ];

    const resetIdleTimer = () => {
      if (idleTimerId !== null) {
        window.clearTimeout(idleTimerId);
      }
      idleTimerId = window.setTimeout(() => logout('idle'), idleTimeoutMs);
    };

    events.forEach((eventName) =>
      window.addEventListener(eventName, resetIdleTimer, { passive: true })
    );
    resetIdleTimer();

    return () => {
      if (idleTimerId !== null) {
        window.clearTimeout(idleTimerId);
      }
      events.forEach((eventName) =>
        window.removeEventListener(eventName, resetIdleTimer)
      );
    };
  }, [isAuthenticated, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
